<template>
  <div class="markdown-editor">
    <!-- Toolbar buttons use @mousedown.prevent so clicking one doesn't take
         focus (and the selection) away from the editor. -->
    <div v-if="editor && editable" class="editor-controls">
      <template v-if="!rawMode">
        <template v-for="(group, gi) in toolbarGroups" :key="gi">
          <button
            v-for="btn in group"
            :key="btn.icon || btn.label"
            class="btn btn-sm btn-ghost"
            :class="{ 'is-active': btn.active?.() }"
            :title="btn.title"
            @mousedown.prevent
            @click="btn.run"
          >
            <span class="icon is-small" :style="btn.labelStyle">
              <font-awesome-icon v-if="btn.icon" :icon="btn.icon" />
              <template v-else>{{ btn.label }}</template>
            </span>
          </button>
          <span class="divider bl-1 bc-light" />
        </template>
        <template v-if="editor.isActive('table')">
          <button
            v-for="btn in tableButtons"
            :key="btn.title"
            class="btn btn-sm btn-ghost"
            :title="btn.title"
            @mousedown.prevent
            @click="btn.run"
          >
            <span class="icon is-small table-label">
              <font-awesome-icon v-if="btn.icon" :icon="btn.icon" />
              <template v-else>{{ btn.label }}</template>
            </span>
          </button>
          <span class="divider bl-1 bc-light" />
        </template>
      </template>

      <button
        class="btn btn-sm btn-ghost"
        :class="{ 'is-active': rawMode }"
        :title="rawMode ? 'Exit raw markdown' : 'Edit raw markdown'"
        @click="toggleRawMode"
      >
        <span class="icon is-small"><font-awesome-icon icon="file-code" /></span>
      </button>
      <button
        v-if="rawMode && editable"
        class="btn btn-sm btn-ghost"
        title="Reset to the source as it was loaded — bypasses the rich-text round trip so you can fix content the WYSIWYG mangled"
        @click="resetRawToSource"
      >
        <span class="icon is-small"><font-awesome-icon icon="rotate-left" /></span>
      </button>
    </div>

    <div v-if="lossySource && !rawMode" class="fidelity-notice flex items-center g-2 text-7">
      <font-awesome-icon icon="triangle-exclamation" />
      <span class="flex-1">
        This document has formatting the rich-text editor can't keep (e.g. raw HTML). Editing here
        will remove it.
      </span>
      <button class="btn btn-sm" @click="editSourceAsRaw">Edit raw markdown</button>
      <button class="btn btn-sm btn-ghost" title="Dismiss" @click="lossySource = false">
        <span class="icon is-small"><font-awesome-icon icon="times" /></span>
      </button>
    </div>

    <!-- Inline link editor -->
    <div v-if="showLinkEditor && !rawMode" class="link-editor flex items-center g-2">
      <input
        ref="linkInput"
        v-model="linkUrl"
        class="input is-small"
        placeholder="Paste or type a URL..."
        @keydown.enter="applyLink"
        @keydown.escape.prevent="closeLinkEditor"
      />
      <button class="btn btn-sm btn-primary" title="Apply" @click="applyLink">
        <span class="icon is-small"><font-awesome-icon icon="check" /></span>
      </button>
      <button
        v-if="editor?.isActive('link')"
        class="btn btn-sm btn-danger btn-outlined"
        title="Remove link"
        @click="removeLink"
      >
        <span class="icon is-small"><font-awesome-icon icon="unlink" /></span>
      </button>
      <button class="btn btn-sm" title="Cancel" @click="closeLinkEditor">
        <span class="icon is-small"><font-awesome-icon icon="times" /></span>
      </button>
    </div>

    <textarea
      v-if="rawMode"
      v-model="rawValue"
      :readonly="!editable"
      class="raw-editor"
      spellcheck="false"
      @input="emit('update:modelValue', rawValue)"
    />
    <EditorContent v-else :editor="editor ?? undefined" class="prose" />
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue"
import { toast } from "vue-sonner"
import { Placeholder } from "@tiptap/extensions"
import { Fragment, type Node as PMNode, Slice } from "@tiptap/pm/model"
import { type AnyExtension, Editor, EditorContent, Extension } from "@tiptap/vue-3"
import {
  isLosslessRoundTrip,
  looksLikeMarkdown,
  markdownExtensions,
  normalizeLinkUrl,
  prepareMarkdown,
  serializeMarkdown,
} from "./markdownExtensions"

interface Props {
  modelValue?: string
  editable?: boolean
  /** Place the caret at the end and grab focus on mount. */
  autofocus?: boolean
  /** Hint shown in an empty editor. */
  placeholder?: string
  /** Extra tiptap extensions supplied by a wrapper (e.g. DocumentEditor's
   *  `@`-mention). Kept generic so MarkdownEditor stays domain-agnostic. */
  extensions?: AnyExtension[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  editable: true,
  autofocus: false,
  placeholder: "Start writing…",
  extensions: () => [],
})

const emit = defineEmits<{
  "update:modelValue": [value: string]
}>()

const editor = shallowRef<Editor | null>(null)
const linkUrl = ref("")
const linkInput = ref<HTMLInputElement>()
const showLinkEditor = ref(false)
const rawMode = ref(false)
const rawValue = ref("")
// Snapshot the markdown the parent gave us at mount, before tiptap parses
// it. The "Reset" button in raw mode restores this so authors can recover
// content the rich-text schema dropped (e.g. literal `<p>`/`<em>` tags).
const sourceSnapshot = ref("")
// True when the loaded source contains markdown the rich-text schema can't
// represent (raw HTML, comments, …). Editing in rich mode would silently drop
// it, so the editor says so up front and offers raw mode instead.
const lossySource = ref(false)

function checkFidelity(source: string) {
  if (!editor.value || !props.editable) {
    lossySource.value = false
    return
  }
  lossySource.value = !isLosslessRoundTrip(source, serializeMarkdown(editor.value))
}

function loadMarkdown(markdown: string) {
  editor.value?.commands.setContent(prepareMarkdown(markdown), {
    emitUpdate: false,
    contentType: "markdown",
  })
}

watch(
  () => props.modelValue,
  (newVal) => {
    if (!editor.value) return
    // While in raw mode the textarea owns the value; ignore external writes
    // so a parent echoing the same string back doesn't yank the user's caret
    // or overwrite a half-typed edit.
    if (rawMode.value) return
    if (serializeMarkdown(editor.value) === (newVal || "")) return
    // Track the authoritative source verbatim so "Reset" in raw mode can
    // restore text the rich-text schema would have dropped.
    sourceSnapshot.value = newVal || ""
    loadMarkdown(newVal || "")
    checkFidelity(newVal || "")
  }
)

watch(
  () => props.editable,
  (val) => {
    editor.value?.setEditable(val)
  }
)

function onUpdate() {
  if (!editor.value) return
  emit("update:modelValue", serializeMarkdown(editor.value))
}

function format(type: string) {
  if (!editor.value) return
  const chain = editor.value.chain().focus()
  const actions: Record<string, () => void> = {
    bold: () => chain.toggleBold().run(),
    italic: () => chain.toggleItalic().run(),
    strikethrough: () => chain.toggleStrike().run(),
    code: () => chain.toggleCode().run(),
  }
  actions[type]?.()
}

function toggleHeading(level: 1 | 2 | 3) {
  editor.value?.chain().focus().toggleHeading({ level }).run()
}

function toList(type: string) {
  if (!editor.value) return
  const chain = editor.value.chain().focus()
  const actions: Record<string, () => void> = {
    bullet: () => chain.toggleBulletList().run(),
    ordered: () => chain.toggleOrderedList().run(),
    task: () => chain.toggleTaskList().run(),
  }
  actions[type]?.()
}

function toggleBlockquote() {
  editor.value?.chain().focus().toggleBlockquote().run()
}

function toggleCodeBlock() {
  editor.value?.chain().focus().toggleCodeBlock().run()
}

function insertHorizontalRule() {
  editor.value?.chain().focus().setHorizontalRule().run()
}

function insertTable() {
  editor.value?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
}

function inTable() {
  return editor.value?.isActive("table") ?? false
}

// Rich-text toolbar, grouped for the dividers between sections. `active` is a
// getter (not a value) so it re-reads editor state on every render, matching
// the inline `editor.isActive(...)` this replaced. A button carries either an
// `icon` or a text `label` (+ optional `labelStyle`).
interface ToolbarButton {
  icon?: string
  label?: string
  labelStyle?: string
  title?: string
  active?: () => boolean | undefined
  run: () => void
}

const toolbarGroups: ToolbarButton[][] = [
  [
    {
      icon: "bold",
      title: "Bold (⌘B)",
      active: () => editor.value?.isActive("bold"),
      run: () => format("bold"),
    },
    {
      icon: "italic",
      title: "Italic (⌘I)",
      active: () => editor.value?.isActive("italic"),
      run: () => format("italic"),
    },
    {
      icon: "strikethrough",
      title: "Strikethrough (⌘⇧S)",
      active: () => editor.value?.isActive("strike"),
      run: () => format("strikethrough"),
    },
    {
      icon: "terminal",
      title: "Inline code (⌘E)",
      active: () => editor.value?.isActive("code"),
      run: () => format("code"),
    },
  ],
  [1, 2, 3].map((level) => ({
    label: `H${level}`,
    title: `Heading ${level}`,
    labelStyle: `font-weight: 700; font-size: ${0.9 - level * 0.05}em`,
    active: () => editor.value?.isActive("heading", { level }),
    run: () => toggleHeading(level as 1 | 2 | 3),
  })),
  [
    {
      icon: "list-ul",
      title: "Bulleted list",
      active: () => editor.value?.isActive("bulletList"),
      run: () => toList("bullet"),
    },
    {
      icon: "list-ol",
      title: "Numbered list",
      active: () => editor.value?.isActive("orderedList"),
      run: () => toList("ordered"),
    },
    {
      icon: "list-check",
      title: "Checklist",
      active: () => editor.value?.isActive("taskList"),
      run: () => toList("task"),
    },
    {
      icon: "quote-left",
      title: "Quote",
      active: () => editor.value?.isActive("blockquote"),
      run: toggleBlockquote,
    },
  ],
  [
    {
      icon: "link",
      title: "Link (⌘K)",
      active: () => editor.value?.isActive("link"),
      run: toggleLinkEditor,
    },
    {
      icon: "code",
      title: "Code block",
      active: () => editor.value?.isActive("codeBlock"),
      run: toggleCodeBlock,
    },
    { icon: "minus", title: "Divider", run: insertHorizontalRule },
    { icon: "table", title: "Insert table", active: inTable, run: insertTable },
  ],
]

// Row/column controls, shown only while the caret is inside a table.
const tableButtons: ToolbarButton[] = [
  {
    label: "+Row",
    title: "Add row below",
    run: () => editor.value?.chain().focus().addRowAfter().run(),
  },
  {
    label: "−Row",
    title: "Delete row",
    run: () => editor.value?.chain().focus().deleteRow().run(),
  },
  {
    label: "+Col",
    title: "Add column right",
    run: () => editor.value?.chain().focus().addColumnAfter().run(),
  },
  {
    label: "−Col",
    title: "Delete column",
    run: () => editor.value?.chain().focus().deleteColumn().run(),
  },
  {
    icon: "trash",
    title: "Delete table",
    run: () => editor.value?.chain().focus().deleteTable().run(),
  },
]

function resetRawToSource() {
  rawValue.value = sourceSnapshot.value
  emit("update:modelValue", sourceSnapshot.value)
}

function toggleRawMode() {
  if (!editor.value) return
  if (rawMode.value) {
    const before = rawValue.value
    const previous = editor.value.getJSON()
    try {
      // contentType:"markdown" is required, otherwise setContent treats the
      // string as HTML and drops anything the schema doesn't recognise (e.g.
      // a fenced code block round-trips as escaped text).
      loadMarkdown(before)
    } catch (err) {
      console.error("[MarkdownEditor] failed to parse raw markdown:", err)
      toast.error("Couldn't parse markdown — fix the syntax or stay in raw mode")
      return
    }
    const after = serializeMarkdown(editor.value)
    // Tiptap silently drops nodes its schema doesn't understand (raw HTML,
    // comments, …). Compare what the markdown renders to, not its text, so
    // harmless normalisation (`_x_` → `*x*`) doesn't trigger this.
    if (
      !isLosslessRoundTrip(before, after) &&
      !window.confirm(
        "The rich-text editor can't keep some of this markdown (e.g. raw HTML or comments) and will remove it.\n\nSwitch anyway? Choose Cancel to keep editing the raw markdown."
      )
    ) {
      editor.value.commands.setContent(previous, { emitUpdate: false })
      return
    }
    rawMode.value = false
    lossySource.value = false
    emit("update:modelValue", after)
  } else {
    rawValue.value = serializeMarkdown(editor.value)
    rawMode.value = true
    closeLinkEditor()
  }
}

/** From the lossy-source notice: edit the untouched source as raw markdown. */
function editSourceAsRaw() {
  rawValue.value = sourceSnapshot.value
  rawMode.value = true
  lossySource.value = false
  closeLinkEditor()
}

// Link editing — auto-opens when cursor is on a link, updates on selection change
function onSelectionUpdate() {
  if (!editor.value) return
  const isOnLink = editor.value.isActive("link")
  if (isOnLink) {
    linkUrl.value = editor.value.getAttributes("link").href || ""
    showLinkEditor.value = true
  } else if (showLinkEditor.value && !linkUrl.value.trim()) {
    // Close if cursor left a link and no URL was being typed
    showLinkEditor.value = false
  }
}

function toggleLinkEditor() {
  if (showLinkEditor.value) {
    closeLinkEditor()
    return
  }
  linkUrl.value = editor.value?.getAttributes("link").href || ""
  showLinkEditor.value = true
  nextTick(() => linkInput.value?.focus())
}

function applyLink() {
  if (!editor.value) return
  const url = normalizeLinkUrl(linkUrl.value)
  const chain = editor.value.chain().focus()
  if (!url) {
    chain.extendMarkRange("link").unsetLink().run()
  } else if (editor.value.state.selection.empty && !editor.value.isActive("link")) {
    // Nothing selected to turn into a link — insert the URL as its own text.
    chain
      .insertContent({ type: "text", text: url, marks: [{ type: "link", attrs: { href: url } }] })
      .unsetMark("link")
      .run()
  } else {
    chain.extendMarkRange("link").setLink({ href: url }).run()
  }
  closeLinkEditor()
}

function removeLink() {
  editor.value?.chain().focus().extendMarkRange("link").unsetLink().run()
  closeLinkEditor()
}

function closeLinkEditor() {
  showLinkEditor.value = false
  linkUrl.value = ""
  editor.value?.commands.focus()
}

// ⌘K opens the link editor (needs this component's state, hence local).
const LinkShortcut = Extension.create({
  name: "markdownEditorLinkShortcut",
  addKeyboardShortcuts() {
    return {
      "Mod-k": () => {
        toggleLinkEditor()
        return true
      },
    }
  },
})

/**
 * Pasting plain-text markdown (e.g. from Claude's copy button or a code
 * editor) inserts it formatted rather than as literal `##`/`**` characters —
 * which the editor would otherwise escape and save as text.
 */
function handlePaste(view: Editor["view"], event: ClipboardEvent): boolean {
  const data = event.clipboardData
  const text = data?.getData("text/plain")
  // Rich clipboard content (from a browser or doc) carries its own HTML —
  // leave that to tiptap's HTML paste.
  if (!editor.value || !text || data?.getData("text/html")) return false
  const { $from } = view.state.selection
  if ($from.parent.type.spec.code) return false
  // Table cells hold inline text only — parsed blocks would split the table.
  if (inTableCell()) return false
  if (!looksLikeMarkdown(text)) return false
  const json = editor.value.markdown?.parse(prepareMarkdown(text))
  if (!json?.content?.length) return false
  return editor.value.commands.insertContent(json.content)
}

function inTableCell() {
  return (
    !!editor.value && (editor.value.isActive("tableCell") || editor.value.isActive("tableHeader"))
  )
}

/**
 * Cells hold paragraphs only, so ProseMirror fits a pasted list or heading by
 * splitting the table around it (losing the rest of the row). Flatten pasted
 * blocks into plain lines instead when pasting inside a cell.
 */
function transformPasted(slice: Slice, view: Editor["view"]): Slice {
  const paragraph = view.state.schema.nodes.paragraph
  if (!inTableCell() || !paragraph) return slice
  const lines: PMNode[] = []
  slice.content.descendants((node) => {
    if (!node.isTextblock) return true
    lines.push(paragraph.create(null, node.content))
    return false
  })
  return lines.length ? new Slice(Fragment.from(lines), 1, 1) : slice
}

onMounted(() => {
  sourceSnapshot.value = props.modelValue || ""
  try {
    editor.value = new Editor({
      content: prepareMarkdown(props.modelValue || ""),
      editable: props.editable,
      extensions: markdownExtensions([
        Placeholder.configure({
          placeholder: () => props.placeholder,
          // The default "is-empty" collides with the app's global empty-state
          // styling (it turns the paragraph into a 200px dashed box).
          emptyNodeClass: "is-editor-empty-node",
        }),
        LinkShortcut,
        ...props.extensions,
      ]),
      contentType: "markdown",
      injectCSS: false,
      editorProps: { handlePaste, transformPasted },
      onUpdate,
      onSelectionUpdate,
    })
    checkFidelity(props.modelValue || "")
    // Focus a collapsed caret at the end. The `autofocus` editor option can
    // land as a range selection over empty content, which reads as odd.
    if (props.autofocus) editor.value.commands.focus("end")
  } catch (err) {
    // Surface init failures rather than rendering an empty toolbar shell
    // with no editor surface and no indication of what went wrong.
    console.error("[MarkdownEditor] failed to initialise:", err)
    toast.error("Editor failed to load")
  }
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})
</script>

<style>
.markdown-editor {
  height: 100%;

  .editor-controls {
    display: flex;
    align-items: center;
    background: var(--auger-bg);
    border: 1px solid var(--auger-border);
    border-bottom: 0;
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 0 4px;
    height: 32px;

    button .icon {
      opacity: 0.8;
      color: var(--auger-text);
    }

    .table-label {
      width: auto;
      font-size: 0.75em;
      font-weight: 600;
    }

    .divider {
      margin-left: 6px;
      margin-right: 6px;
      height: 18px;
    }
  }

  .fidelity-notice {
    background: var(--auger-bg-subtle);
    border: 1px solid var(--auger-border);
    border-top: 0;
    padding: 0.35rem 0.5rem;
    color: var(--auger-text);

    .flex-1 {
      flex: 1;
    }
  }

  .link-editor {
    background: var(--auger-bg-subtle);
    border: 1px solid var(--auger-border);
    border-top: 0;
    padding: 0.35rem 0.5rem;

    input {
      flex: 1;
    }
  }

  .raw-editor {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    min-height: 300px;
    padding: 1rem;
    border: 1px solid var(--auger-border);
    border-top: 0;
    border-radius: var(--auger-radius);
    background: var(--auger-bg);
    color: var(--auger-text);
    font-family: var(--auger-font-mono);
    font-size: var(--auger-size-6);
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
    resize: vertical;
    outline: none;
  }

  /* Editor chrome only. Typography (headings, lists, code, links, …) comes
     from the shared .prose class applied to EditorContent above — see
     packages/ui/src/styles/prose.css — so the editing surface matches the read view. */
  .ProseMirror {
    box-sizing: border-box;
    font-family: inherit;
    overflow: auto;
    white-space: pre-wrap;
    background: var(--auger-bg);

    &[contenteditable="true"] {
      border: 1px solid var(--auger-border);
      border-bottom-left-radius: 2px;
      border-bottom-right-radius: 2px;
      resize: vertical;
      min-height: 300px;
      padding: 1rem;
    }

    /* Links are non-interactive while editing; colour/underline are inherited
       from .prose. */
    a.editor-link {
      cursor: text;
      pointer-events: none;
    }

    /* Cell borders/padding come from .prose; this is editing-only feedback. */
    .selectedCell {
      background: var(--auger-primary-light);
    }

    img.ProseMirror-selectednode {
      outline: 2px solid var(--auger-primary);
    }

    /* Placeholder hint on an empty document. */
    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      color: var(--auger-text-muted);
      float: left;
      height: 0;
      pointer-events: none;
    }
  }

  .ProseMirror-focused {
    outline-style: none;
  }
}
</style>
